import { FC, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  Drawer,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { alpha } from '@mui/material/styles';
import { userService } from 'src/services/User.service';
import { certTypeDisplayLabel } from '../utils/certTerminology';

interface MemberDoc {
  document_title: string;
  certification_id: number;
  cert_type: string;
  property_name: string;
  effective_date: string | null;
  document_id: number;
  is_unsigned: boolean;
}

interface MemberInfo {
  user_id: number;
  first_name: string;
  last_name: string;
  has_esign_saved: boolean;
  has_initials_saved: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  certificationId: number | null;
  memberUserId: number | null;
  memberFirstName: string;
  memberLastName: string;
  avatarColor: string;
  avatarFgColor: string;
  initials: string;
}

const MemberDocsModal: FC<Props> = ({
  open,
  onClose,
  certificationId,
  memberUserId,
  memberFirstName,
  memberLastName,
  avatarColor,
  avatarFgColor,
  initials,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(false);
  const [member, setMember] = useState<MemberInfo | null>(null);
  const [documents, setDocuments] = useState<MemberDoc[]>([]);

  useEffect(() => {
    if (!open || !certificationId || !memberUserId) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    userService
      .fetchMemberUnsignedDocs(certificationId, memberUserId)
      .then((data) => {
        if (!cancelled) {
          setMember(data.member ?? null);
          setDocuments(data.documents ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDocuments([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, certificationId, memberUserId]);

  const displayName =
    member?.first_name || memberFirstName
      ? `${member?.first_name || memberFirstName} ${
          member?.last_name || memberLastName
        }`.trim()
      : '';

  const content = (
    <Stack gap={2} sx={{ p: 3, pb: 4 }}>
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            backgroundColor: avatarColor,
            color: avatarFgColor,
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          {initials}
        </Avatar>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {displayName}
          </Typography>
          {member && (
            <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
              {member.has_esign_saved ? (
                <Typography
                  variant="caption"
                  sx={{
                    bgcolor: 'success.lighter',
                    color: 'success.dark',
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontWeight: 600,
                  }}
                >
                  eSign saved
                </Typography>
              ) : (
                <Typography
                  variant="caption"
                  sx={{
                    bgcolor: 'error.lighter',
                    color: 'error.dark',
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontWeight: 600,
                  }}
                >
                  No signature saved
                </Typography>
              )}
              {member.has_initials_saved ? (
                <Typography
                  variant="caption"
                  sx={{
                    bgcolor: 'success.lighter',
                    color: 'success.dark',
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontWeight: 600,
                  }}
                >
                  Initials saved
                </Typography>
              ) : (
                <Typography
                  variant="caption"
                  sx={{
                    bgcolor: 'error.lighter',
                    color: 'error.dark',
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontWeight: 600,
                  }}
                >
                  No Initials Saved
                </Typography>
              )}
            </Stack>
          )}
        </Box>
      </Stack>

      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        {loading
          ? 'Loading...'
          : documents.length === 0
          ? 'No unsigned documents found.'
          : `${documents.length} unsigned document${
              documents.length === 1 ? '' : 's'
            }`}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Stack gap={1}>
          {documents.map((doc) => {
            const effYear = doc.effective_date
              ? new Date(doc.effective_date).getFullYear()
              : null;

            return (
              <Stack
                key={`${doc.certification_id}-${doc.document_id}`}
                direction="row"
                alignItems="center"
                gap={1.5}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.error.main, 0.12),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <InsertDriveFileOutlinedIcon
                    sx={{ fontSize: 22, color: 'error.main' }}
                  />
                </Box>
                <Stack
                  sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
                  gap={0}
                >
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {doc.document_title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[certTypeDisplayLabel(doc.cert_type), effYear]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                  {doc.property_name && (
                    <Typography variant="caption" color="text.secondary">
                      {doc.property_name}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Stack>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { borderRadius: '16px 16px 0 0', maxHeight: '85vh' },
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      {content}
    </Dialog>
  );
};

export default MemberDocsModal;
